/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { DeploymentItemDetails } from "@src/@types/MainItem";
import { MinionPool } from "@src/@types/MinionPool";
import { Network } from "@src/@types/Network";
import MainDetails from "@src/components/modules/TransferModule/MainDetails";
import Tasks from "@src/components/modules/TransferModule/Tasks";
import LiveProgress from "@src/components/modules/TransferModule/LiveProgress";
import {
  ActionButton,
  DetailsActions,
  DetailsTabs,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import { CircleX, Clock, Info, ListChecks, Trash2 } from "lucide-react";
import { RollbackCard, rollbackView } from "@src/components/modules/CutoverModule/Rollback";
import type { RollbackDeployment } from "@src/components/modules/CutoverModule/Rollback";
import EvidenceButton from "@src/components/modules/TransferModule/EvidenceButton";
import AccessCard from "@src/components/modules/AccessModule";

import type { Instance } from "@src/@types/Instance";
import type { Endpoint, StorageBackend } from "@src/@types/Endpoint";
import type { Field } from "@src/@types/Field";

const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;
const DetailsBody = styled.div<any>`
  width: 100%;
  min-width: 0;
`;

const NavigationItems = [
  {
    label: "Overview",
    value: "",
    icon: Info,
  },
  {
    label: "Tasks",
    value: "tasks",
    icon: ListChecks,
  },
];

type Props = {
  item: DeploymentItemDetails | null;
  itemId: string;
  minionPools: MinionPool[];
  detailsLoading: boolean;
  storageBackends: StorageBackend[];
  instancesDetails: Instance[];
  instancesDetailsLoading: boolean;
  networks: Network[];
  sourceSchema: Field[];
  sourceSchemaLoading: boolean;
  destinationSchema: Field[];
  destinationSchemaLoading: boolean;
  endpoints: Endpoint[];
  page: string;
  onDeleteDeploymentClick: () => void;
  onShowExecutionClick: () => void;
  /** Swift: opens the rollback dialog. */
  onRollbackClick?: () => void;
};
@observer
class DeploymentDetailsContent extends React.Component<Props> {
  renderBottomControls() {
    return (
      <DetailsActions>
        {this.props.item ? <EvidenceButton kind="deployment" id={this.props.item.id} /> : <div />}
        <ActionButton
          variant="danger"
          onClick={this.props.onDeleteDeploymentClick}
          disabled={(this.props.item as RollbackDeployment | null)?.rollback_status === "running"}
          title={
            (this.props.item as RollbackDeployment | null)?.rollback_status === "running"
              ? "A deployment that is being rolled back cannot be deleted"
              : undefined
          }
        >
          <Trash2 size={16} />
          Delete deployment
        </ActionButton>
      </DetailsActions>
    );
  }

  renderMainDetails() {
    if (this.props.page !== "") {
      return null;
    }

    return (
      <MainColumn>
        {this.renderRollback()}
        {this.props.item ? (
          <AccessCard
            deploymentId={this.props.item.id}
            completed={this.props.item.last_execution_status === "COMPLETED"}
          />
        ) : null}
      <MainDetails
        item={this.props.item}
        storageBackends={this.props.storageBackends}
        minionPools={this.props.minionPools}
        instancesDetails={this.props.instancesDetails}
        instancesDetailsLoading={this.props.instancesDetailsLoading}
        networks={this.props.networks}
        sourceSchema={this.props.sourceSchema}
        sourceSchemaLoading={this.props.sourceSchemaLoading}
        destinationSchema={this.props.destinationSchema}
        destinationSchemaLoading={this.props.destinationSchemaLoading}
        endpoints={this.props.endpoints}
        bottomControls={this.renderBottomControls()}
        loading={this.props.detailsLoading}
      />
      </MainColumn>
    );
  }

  renderRollback() {
    const item = this.props.item as RollbackDeployment | null;
    if (!item || rollbackView(item) === "none") {
      return null;
    }
    const endpointType = (id?: string) =>
      this.props.endpoints.find(e => e.id === id)?.type || null;
    return (
      <RollbackCard
        deployment={item}
        sourceType={endpointType(item.origin_endpoint_id)}
        targetType={endpointType(item.destination_endpoint_id)}
        instancesDetails={this.props.instancesDetails}
        onRollbackClick={() => this.props.onRollbackClick?.()}
      />
    );
  }

  renderTasks() {
    if (this.props.page !== "tasks" || !this.props.item?.tasks) {
      return null;
    }

    const { item } = this.props;
    const endpointType = (id?: string) =>
      this.props.endpoints.find(e => e.id === id)?.type || null;
    return (
      <>
        {(item as RollbackDeployment).rollback_status ? (
          <div style={{ marginBottom: 16 }}>{this.renderRollback()}</div>
        ) : null}
        {/* Live progress, throughput, ETA and the migration animation while the deployment runs. */}
        <LiveProgress
          tasks={item.tasks || []}
          status={item.last_execution_status}
          createdAt={item.created_at}
          updatedAt={item.updated_at}
          executionType="deployment"
          sourceType={endpointType(item.origin_endpoint_id)}
          targetType={endpointType(item.destination_endpoint_id)}
          sampleKey={item.id}
        />
        <Tasks
          items={item.tasks}
          loading={this.props.detailsLoading}
          instancesDetails={this.props.instancesDetails}
        />
      </>
    );
  }

  renderPendingMessage() {
    const { item } = this.props;
    if (!item) {
      return null;
    }

    if (item.last_execution_status === "PENDING") {
      return (
        <SectionCard>
          <EmptyState
            compact
            icon={Clock}
            tone="neutral"
            title="Waiting for the deployer execution"
            message="This deployment starts when the transfer execution that deploys it finishes."
            secondaryLabel="Show execution"
            onSecondary={this.props.onShowExecutionClick}
          />
        </SectionCard>
      );
    }

    if (item.last_execution_status === "ERROR") {
      return (
        <SectionCard>
          <EmptyState
            compact
            icon={CircleX}
            tone="danger"
            title="The deployer execution did not complete"
            message="Open the execution to see which task failed."
            secondaryLabel="Show execution"
            onSecondary={this.props.onShowExecutionClick}
          />
        </SectionCard>
      );
    }

    return null;
  }

  render() {
    return (
      <Wrapper>
        <DetailsTabs
          items={NavigationItems}
          selectedValue={this.props.page}
          href={item =>
            `/deployments/${this.props.itemId}${item.value ? `/${item.value}` : ""}`
          }
        />
        <DetailsBody>
          {this.renderMainDetails()}
          {this.renderTasks()}
          {this.props.page === "tasks" && !this.props.item?.tasks
            ? this.renderPendingMessage()
            : null}
        </DetailsBody>
      </Wrapper>
    );
  }
}

export default DeploymentDetailsContent;
